import React from 'react';
import { cn } from '@/lib/cn';

type TabBackground = 'primary' | 'secondary';

export interface TabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Background variant - primary has no background when inactive, secondary has gray background */
  background?: TabBackground;
  /** Whether the tab is the active/selected one */
  active?: boolean;
  /** Icon to display in the tab (React component or element) */
  icon?: React.ReactNode;
  /** Optional badge to display in the tab. Hidden unless provided. */
  badge?: React.ReactNode;
}

const baseStyles = cn(
  'relative cursor-pointer transition-all duration-200 ease-in-out',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-accent',
  'disabled:opacity-60 disabled:cursor-not-allowed',
);

const containerStyles = cn(
  'flex items-center justify-center gap-2 px-3 py-2 rounded-lg',
  'transition-all duration-200 ease-in-out',
);

const iconContainerStyles = cn(
  'flex items-center justify-center p-2 rounded-full shrink-0',
  'transition-all duration-200 ease-in-out',
);

const textStyles = cn(
  'text-sm leading-5 whitespace-nowrap',
  'transition-all duration-200 ease-in-out',
);

function getTabStyles(background: TabBackground, active: boolean) {
  const borderClass = active ? 'border-b-2 border-accent' : 'border-b-2 border-transparent';

  if (background === 'primary') {
    return {
      wrapper: cn(
        borderClass,
        'pb-1', // 4px padding bottom for the border
      ),
      container: 'hover:bg-secondary',
      iconContainer: active
        ? 'bg-active' // #ecf1ff - accent background when active
        : 'bg-secondary hover:bg-secondary active:bg-secondary', // Secondary background when inactive
      iconColor: active
        ? 'icon-accent' // #5c5cff - accent icon when active
        : 'icon-secondary', // #475569 - secondary icon when inactive
      text: active
        ? 'text-accent font-semibold' // #5c5cff - accent text when active
        : 'text-secondary hover:text-secondary active:text-secondary font-normal', // #475569 - secondary text when inactive
    };
  } else {
    // Secondary background
    return {
      wrapper: cn(
        borderClass,
        'pb-1', // 4px padding bottom for the border
      ),
      container: 'hover:bg-secondary-hover',
      iconContainer: active
        ? 'bg-active' // #ecf1ff - accent background when active
        : 'bg-tertiary hover:bg-secondary-hover active:bg-secondary-hover', // Secondary background when inactive
      iconColor: active
        ? 'icon-accent' // #5c5cff - accent icon when active
        : 'icon-secondary', // #475569 - secondary icon when inactive
      text: active
        ? 'text-accent font-semibold' // #5c5cff - accent text when active
        : 'text-secondary hover:text-secondary active:text-secondary font-normal', // #475569 - secondary text when inactive
    };
  }
}

export function Tab({
  background = 'primary',
  active = false,
  icon,
  badge,
  className,
  children,
  ...props
}: TabProps) {
  const styles = getTabStyles(background, active);

  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(baseStyles, styles.wrapper, className)}
      {...props}
    >
      <div className={cn(containerStyles, styles.container)}>
        {icon && (
          <div className={cn(iconContainerStyles, styles.iconContainer)}>
            <div className={cn('w-4 h-4 shrink-0', styles.iconColor)}>{icon}</div>
          </div>
        )}
        <span className={cn(textStyles, styles.text)}>{children}</span>
        {badge && <span className="flex items-center shrink-0">{badge}</span>}
      </div>
    </button>
  );
}

export default Tab;
